/**
 * @module api/domain/user
 * @description 사용자/관리자/수리파트너 관련 API
 *   - 수리파트너 소속/직원 CRUD
 *   - 관리자 소속/계정 CRUD, 비밀번호 초기화
 *   - 유저 그룹/타입 조회, 파트너 관계 조회
 */
import { getApi, api, deleteApi, noCustomApi } from '@/api/api'

/** 수리파트너 소속 타입 조회 */
export const getStaffTeamType = async () => {
  const url = '/user/repair-staff-teams/type'
  return await getApi(url)
}

/** 수리파트너 소속 데이터 조회 */
export const getRepairStaffTeamDetail = async (id) => {
  const url = `/user/repair-staff-teams/${id}`
  return await getApi(url)
}

/** 수리파트너 소속 리스트 조회 */
export const getRepairStaffTeamList = async (partnerId) => {
  const url = partnerId
    ? `/user/repair-staff-teams/all?partnerId=${partnerId}`
    : '/user/repair-staff-teams/all'
  return await getApi(url)
}

/** 수리파트너 직원 전체 조회 */
export const getAllStaff = async (staffTeamId) => {
  const url = `/user/repair-staffs/${staffTeamId}/all`
  return await getApi(url)
}

/** 수리파트너 직원 데이터 조회 */
export const getRepairStaffDetail = async (id) => {
  const url = `/user/repair-staffs/${id}`
  return await getApi(url)
}

/** 관리자 소속 리스트 조회 */
export const getAdminTeamList = async () => {
  const url = '/user-groups/all'
  return await getApi(url)
}

/** 관리자 소속 타입 리스트 조회 */
export const getAdminTeamTypeList = async () => {
  const url = '/users/types'
  return await getApi(url)
}

/** 관리자 소속 상세 조회 */
export const getAdminTeamDetail = async (id) => {
  const url = `/user-groups/${id}`
  return await getApi(url)
}

/** 관리자 상세 조회 */
export const getAdminDetail = async (loginId) => {
  const url = `/users/${loginId}`
  return await getApi(url)
}

/** 사용자 상세 조회 */
export const getUserDetail = async (loginId) => {
  const url = `/my-info/${loginId}`
  return await getApi(url)
}

/** 관리자 소속-파트너 관계 조회 */
export const getAdminTeamPartnerRelation = async (userGroupId) => {
  const url = `/user-groups/partner/allocation/${userGroupId}`
  return await getApi(url)
}

/** 관리자 파트너 리스트 조회 */
export const getAdminPartnerList = async (userGroupId) => {
  const url = `/user-groups/partner/allocation/admin/${userGroupId}`
  return await getApi(url)
}

/** 수리파트너 소속-파트너 관계 조회 */
export const getRepairStaffTeamPartnerRelation = async (staffTeamId) => {
  const url = '/repair-staff-team-partner/relation?repairStaffTeamId=' + staffTeamId
  return await getApi(url)
}

/** 개인 정보 수정 */
export const updateMyInfo = async (loginId, method, data) => {
  const url = `/my-info/${loginId}`
  return await api(url, method, data)
}

/** 수리파트너 소속 등록 */
export const saveRepairStaffTeam = async (method, data) => {
  const url = '/user/repair-staff-teams'
  return await api(url, method, data)
}

/** 수리파트너 소속 수정 */
export const updateRepairStaffTeam = async (teamId, method, data) => {
  const url = `/user/repair-staff-teams/${teamId}`
  return await api(url, method, data)
}

/** 수리파트너 소속 삭제 */
export const deleteRepairStaffTeam = async (teamId, method) => {
  const url = `/user/repair-staff-teams/${teamId}/delete`
  return await deleteApi(url, method, null)
}

/** 수리파트너 직원 등록 */
export const saveRepairStaff = async (method, data) => {
  const url = '/user/repair-staffs'
  return await api(url, method, data)
}

/** 수리파트너 직원 수정 */
export const updateRepairStaff = async (loginId, method, data) => {
  const url = `/user/repair-staffs/${loginId}`
  return await api(url, method, data)
}

/** 수리파트너 직원 삭제 */
export const deleteRepairStaff = async (loginId, method) => {
  const url = `/user/repair-staffs/${loginId}`
  return await deleteApi(url, method, null)
}

/** 관리자 소속 등록 */
export const saveAdminTeam = async (method, data) => {
  const url = '/user-groups'
  return await api(url, method, data)
}

/** 관리자 소속 수정 */
export const updateAdminTeam = async (id, method, data) => {
  const url = `/user-groups/${id}`
  return await api(url, method, data)
}

/** 관리자 소속 삭제 */
export const deleteAdminTeam = async (id, method) => {
  const url = `/user-groups/${id}/delete`
  return await deleteApi(url, method, null)
}

/** 관리자 등록 */
export const saveAdmin = async (method, data) => {
  const url = '/users'
  return await api(url, method, data)
}

/** 관리자 수정 */
export const updateAdmin = async (loginId, method, data) => {
  const url = `/users/${loginId}`
  return await api(url, method, data)
}

/** 관리자 삭제 */
export const deleteAdmin = async (loginId, method) => {
  const url = `/users/${loginId}`
  return await deleteApi(url, method, null)
}

/** 수리파트너 직원 비밀번호 초기화 */
export const resetRepairStaffPassword = async (loginId, method) => {
  const url = `/user/repair-staffs/${loginId}/pw/reset`
  return await noCustomApi(url, method, null)
}

/** 관리자 비밀번호 초기화 */
export const resetAdminPassword = async (loginId, method) => {
  const url = `/users/${loginId}/pw/reset`
  return await noCustomApi(url, method, null)
}

/** 유저 그룹 전체 조회 */
export const getAllUserGroups = async () => {
  const url = '/user-groups/all'
  return await getApi(url)
}

/** 소속 타입 조회 */
export const getUserTypeList = async () => {
  const url = '/users/types'
  return await getApi(url)
}
